import React from "react";
import { Link, Navigate, useLocation } from "@tanstack/react-router";
import { Building2, ShieldCheck } from "lucide-react";
import { useAuth } from "@core/auth/AuthProvider";
import { AppHeader } from "@core/layout/AppHeader";
import { MobileNav } from "@core/layout/MobileNav";
import { SidebarNav } from "@core/layout/SidebarNav";
import { useOrganization } from "@core/organizations/OrganizationProvider";
import { useAppPreferences } from "@core/preferences/AppPreferencesProvider";

export function AppShell({ children }) {
  const location = useLocation();
  const { user, isLoading, signOut } = useAuth();
  const {
    activeOrganization,
    isLoading: isOrganizationLoading,
    error: organizationError,
    isSuperadmin,
    refreshOrganizations,
  } = useOrganization();
  const { preferences, setPreference } = useAppPreferences();
  const [sidebarHovered, setSidebarHovered] = React.useState(false);
  const collapseMode = preferences.sidebarCollapseMode ?? "dynamic";

  React.useEffect(() => {
    setSidebarHovered(false);
  }, [location.href]);

  const sidebarExpanded = !preferences.sidebarCollapsed
    && preferences.sidebarDisplayMode !== "icon-only"
    && (
      collapseMode === "expanded"
      || (collapseMode === "dynamic" && (preferences.sidebarPinned || sidebarHovered))
    );
  const compactSidebar = !sidebarExpanded || preferences.sidebarBehavior === "overlay";
  const displayClass = preferences.sidebarDisplayMode === "icon-and-text-compact"
    ? "sidebar-display-compact"
    : preferences.sidebarDisplayMode === "text-only"
      ? "sidebar-display-text-only"
      : "";

  const closeOverlaySidebar = React.useCallback(() => {
    setSidebarHovered(false);
    if (collapseMode === "dynamic") {
      setPreference("sidebarPinned", false);
      return;
    }
    setPreference("sidebarCollapsed", true);
  }, [collapseMode, setPreference]);

  const isAuthRoute = location.pathname === "/login" || location.pathname === "/signup";

  if (isLoading || (user && isOrganizationLoading)) {
    return <main className="center-screen" aria-live="polite">Loading secure workspace…</main>;
  }

  if (!user) {
    if (isAuthRoute) return children;
    return <Navigate to="/login" replace />;
  }

  if (isAuthRoute) {
    return <Navigate to="/jobs" replace />;
  }

  if (!organizationError && isSuperadmin && !activeOrganization && location.pathname.startsWith("/admin")) {
    return <div className="platform-admin-empty-shell"><main className="main-content">{children}</main></div>;
  }

  if (organizationError || !activeOrganization) {
    return (
      <main className="center-screen organization-access-state">
        {isSuperadmin ? <ShieldCheck size={30} aria-hidden="true" /> : <Building2 size={30} aria-hidden="true" />}
        <h1>{organizationError ? "Business access could not be loaded" : isSuperadmin ? "No VenPro businesses yet" : "No vendor business access"}</h1>
        <p className="muted">
          {organizationError
            ? "Your identity is signed in, but organization membership could not be verified."
            : isSuperadmin ? "Create the first business from the VenPro administration workspace." : "Ask a vendor owner to add this identity to an organization."}
        </p>
        <div className="access-state-actions">
          {organizationError && (
            <button className="button button-primary" type="button" onClick={() => refreshOrganizations()}>Try again</button>
          )}
          {!organizationError && isSuperadmin && <Link className="button button-primary" to="/admin/businesses">Open administration</Link>}
          <button className="button button-secondary" type="button" onClick={signOut}>Sign out</button>
        </div>
      </main>
    );
  }

  return (
    <div
      className={`venpro-shell topbar-${preferences.topbarItemSize} mobilebar-${preferences.mobileAppbarVisible ? preferences.mobileAppbarSize : "hidden"} ${compactSidebar ? "sidebar-collapsed" : ""} ${preferences.sidebarBehavior === "overlay" ? "sidebar-overlay" : "sidebar-inline"} ${displayClass} ${preferences.topbarVisible ? "topbar-visible" : "topbar-hidden"}`}
    >
      <AppHeader />
      <SidebarNav
        expanded={sidebarExpanded}
        onHoverChange={setSidebarHovered}
      />
      {collapseMode === "dynamic" && sidebarExpanded && (
        <button
          className="vp-sidebar-clickaway"
          type="button"
          onClick={closeOverlaySidebar}
          aria-label="Close sidebar"
        />
      )}
      <div className="vp-app-inset">
        <main className={`main-content ${location.pathname.startsWith("/settings") ? "main-content-settings" : ""}`}>{children}</main>
        <MobileNav />
      </div>
    </div>
  );
}
